/**
 * Scrubbing for crash reports from the owner app and the worker page (company standard, rule 4 and
 * section 5): no emails, sign-in or link codes, instruction text, or worker link codes. Reports keep
 * only what failed, where in the code, and the screen's route pattern.
 */
const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const CODES = /\b[0-9A-Za-z]{14}\b|[A-Za-z0-9_-]{32,}|\b\d{6}\b/g;

export function scrubText(text: string): string {
  return text
    .replace(/params:[\s\S]*/, 'params: [removed]') // saved values listed after "params:"
    .replace(EMAIL, '[email]')
    .replace(CODES, '[code]');
}

/** A screen's address without anything private: worker link codes and ids become placeholders. */
export function routePattern(path: string): string {
  return path
    .replace(/^\/s\/[^/]+/, '/s/[link]')
    .replace(/^\/[0-9A-Za-z]{14}$/, '/[link]')
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '[id]');
}

interface EventLike {
  message?: string;
  request?: unknown;
  user?: unknown;
  extra?: unknown;
  breadcrumbs?: unknown;
  tags?: Record<string, unknown>;
  exception?: { values?: { value?: string; stacktrace?: { frames?: { vars?: unknown; pre_context?: unknown; context_line?: unknown; post_context?: unknown }[] } }[] };
}

/** Sentry's beforeSend for browsers: strip everything that could carry personal data. */
export function scrubEvent<T extends EventLike>(event: T, path: string): T {
  delete event.request;
  delete event.user;
  delete event.extra;
  delete event.breadcrumbs;
  if (event.message) event.message = scrubText(event.message);
  for (const ex of event.exception?.values ?? []) {
    if (ex.value) ex.value = scrubText(ex.value);
    for (const f of ex.stacktrace?.frames ?? []) {
      delete f.vars;
      delete f.pre_context;
      delete f.context_line;
      delete f.post_context;
    }
  }
  event.tags = { ...event.tags, screen: routePattern(path) };
  return event;
}
